import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { 
  Target, Loader, CheckCircle, AlertCircle, XCircle, TrendingUp, 
  Zap, Shield, Award, AlertTriangle, ChevronRight, Info, RefreshCw,
  Edit3, Trash2, Plus
} from 'lucide-react';
import { trackOnboardingMilestone } from '../utils/onboarding';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function QualityScoreOptimizer({ selectedAccount }) {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;
  
  // Selection state
  const [campaigns, setCampaigns] = useState([]);
  const [adGroups, setAdGroups] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedAdGroup, setSelectedAdGroup] = useState('');
  
  // Data state
  const [adGroupData, setAdGroupData] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [improvements, setImprovements] = useState(null);
  
  // UI state
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingAdGroups, setLoadingAdGroups] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingImprovements, setGeneratingImprovements] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [showPushConfirm, setShowPushConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [pushSuccess, setPushSuccess] = useState(false);
  
  // Load campaigns on mount
  useEffect(() => {
    loadCampaigns();
    trackOnboardingMilestone('run_quality_score');
  }, []);
  
  // Load ad groups when campaign selected
  useEffect(() => {
    if (selectedCampaign) {
      loadAdGroups(selectedCampaign);
      setSelectedAdGroup('');
      setAdGroupData(null);
      setAnalysis(null);
    }
  }, [selectedCampaign]);
  
  // Load ad group details and analyze when selected
  useEffect(() => {
    if (selectedAdGroup) {
      loadAdGroupDetails(selectedAdGroup);
    }
  }, [selectedAdGroup]);
  
  const loadCampaigns = async () => {
    try {
      setLoadingCampaigns(true);
      setError(null);
      const token = await getToken();
      
      // Use selected account ID
      const accountId = selectedAccount?.customerId || selectedAccount?.id;
      const url = accountId 
        ? `${API_URL}/api/quality-score/campaigns?accountId=${accountId}`
        : `${API_URL}/api/quality-score/campaigns`;
      
      console.log('Loading campaigns for account:', accountId);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load campaigns');
      }
      
      // Check if this is a manager account
      if (data.isManager) {
        setError(
          data.message || 
          'This is a Manager (MCC) account. Please go to Settings and select a specific client account to analyze campaigns.'
        );
        setCampaigns([]);
        return;
      }
      
      setCampaigns(data.campaigns);
      
      // Show friendly message if no Search campaigns found
      if (data.campaigns.length === 0) {
        setError('No Search campaigns found with impressions. Quality Score analysis is only available for Search campaigns.');
      }
      
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError(err.message);
    } finally {
      setLoadingCampaigns(false);
    }
  };
  
  const loadAdGroups = async (campaignId) => {
    try {
      setLoadingAdGroups(true);
      setError(null);
      const token = await getToken();
      
      const accountId = selectedAccount?.customerId || selectedAccount?.id;
      const url = accountId
        ? `${API_URL}/api/quality-score/ad-groups?campaignId=${campaignId}&accountId=${accountId}`
        : `${API_URL}/api/quality-score/ad-groups?campaignId=${campaignId}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load ad groups');
      }
      
      setAdGroups(data.adGroups);
      
    } catch (err) {
      console.error('Error loading ad groups:', err);
      setError(err.message);
    } finally {
      setLoadingAdGroups(false);
    }
  };
  
  const loadAdGroupDetails = async (adGroupId) => {
    try {
      setLoadingDetails(true);
      setError(null);
      const token = await getToken();
      
      const accountId = selectedAccount?.customerId || selectedAccount?.id;
      let url = `${API_URL}/api/quality-score/ad-group-details?adGroupId=${adGroupId}&campaignId=${selectedCampaign}`;
      if (accountId) {
        url += `&accountId=${accountId}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load ad group details');
      }
      
      setAdGroupData(data.adGroup);
      
      // Auto-analyze
      await analyzeAdGroup(data.adGroup);
      
    } catch (err) {
      console.error('Error loading ad group details:', err);
      setError(err.message);
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const analyzeAdGroup = async (adGroup) => {
    try {
      setAnalyzing(true);
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/api/quality-score/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          adGroupName: adGroup.name,
          keywords: adGroup.keywords,
          headlines: adGroup.headlines,
          descriptions: adGroup.descriptions,
          landingPageUrl: adGroup.landingPageUrl,
          qualityScore: adGroup.qualityScore,
          impressions: adGroup.impressions,
          accountId: adGroup.accountId,
          campaignId: adGroup.campaignId,
          adGroupId: adGroup.id
        })
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Analysis failed');
      }
      
      setAnalysis(data.analysis);
      
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };
  
  const generateImprovements = async () => {
    try {
      setGeneratingImprovements(true);
      setError(null);
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/api/quality-score/generate-improvements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          analysis,
          currentAd: {
            adGroupName: adGroupData.name,
            keywords: adGroupData.keywords,
            headlines: adGroupData.headlines,
            descriptions: adGroupData.descriptions
          }
        })
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to generate improvements');
      }
      
      setImprovements(data.improvements);
      
    } catch (err) {
      console.error('Error generating improvements:', err);
      setError(err.message);
    } finally {
      setGeneratingImprovements(false);
    }
  };
  
  const pushToGoogleAds = async () => {
    try {
      setPushing(true);
      setPushSuccess(false);
      setError(null);
      const token = await getToken();
      
      const accountId = selectedAccount?.customerId || selectedAccount?.id;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      const response = await fetch(`${API_URL}/api/quality-score/push-to-ads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accountId,
          campaignId: adGroupData.campaignId,
          adGroupId: adGroupData.id,
          headlines: improvements.headlines,
          descriptions: improvements.descriptions,
          landingPageUrl: adGroupData.landingPageUrl,
          label: 'Bidoryx Optimized'
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to push to Google Ads');
      }
      
      setPushSuccess(true);
      setShowPushConfirm(false);
      
    } catch (err) {
      console.error('Error pushing to Google Ads:', err);
      setError(err.message);
    } finally {
      setPushing(false);
    }
  };
  
  const getStatusIcon = (status) => {
    if (status === 'above_average') return <CheckCircle size={20} color="#10b981" />;
    if (status === 'average') return <Info size={20} color="#f59e0b" />;
    return <XCircle size={20} color="#ef4444" />;
  };
  
  const getStatusColor = (status) => {
    if (status === 'above_average') return '#10b981';
    if (status === 'average') return '#f59e0b';
    return '#ef4444';
  };
  
  const getPriorityColor = (level) => {
    if (level === 'critical') return '#dc2626';
    if (level === 'high') return '#ea580c';
    if (level === 'medium') return '#f59e0b';
    return '#22c55e';
  };
  
  if (loadingCampaigns) {
    return (
      <div style={{
        padding: '60px 20px',
        maxWidth: '600px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <Loader size={64} style={{
          animation: 'spin 1s linear infinite',
          margin: '0 auto 24px',
          color: '#10b981'
        }} />
        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          marginBottom: '12px',
          color: c.text.primary
        }}>
          Loading campaigns...
        </h2>
      </div>
    );
  }
  
  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 28 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <Award size={24} color="#fff" />
        </div>
        <div>
          <h1 style={{ color: c.text.primary, fontSize: 26, fontWeight: 700, margin: 0 }}>
            Quality Score Optimizer
          </h1>
          <p style={{ color: c.text.secondary, fontSize: 14, margin: '4px 0 0' }}>
            AI-powered analysis of your Search ad groups — automatically loaded from Google Ads.
            Only Search campaigns are shown.
          </p>
        </div>
      </div>
      
      {/* Error Display */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '10px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <AlertCircle size={20} color="#ef4444" />
          <span style={{ color: theme.mode === 'light' ? '#991b1b' : '#fca5a5' }}>{error}</span>
        </div>
      )}
      
      {/* Selection Panel */}
      <div style={{
        background: c.bg.secondary,
        border: `1px solid ${c.border.primary}`,
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h2 style={{
          fontSize: '16px',
          fontWeight: '600',
          marginBottom: '20px',
          color: c.text.primary
        }}>
          Select Ad Group to Analyze
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Campaign Selector */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: c.text.secondary,
              fontSize: '13px',
              fontWeight: '600'
            }}>
              Campaign
            </label>
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              disabled={campaigns.length === 0}
              style={{
                width: '100%',
                padding: '12px',
                background: c.bg.tertiary,
                border: `1px solid ${c.border.primary}`,
                borderRadius: '8px',
                color: c.text.primary,
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">Select a campaign...</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name} - Search ({campaign.impressions.toLocaleString()} imp)
                </option>
              ))}
            </select>
          </div>
          
          {/* Ad Group Selector */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: c.text.secondary,
              fontSize: '13px',
              fontWeight: '600'
            }}>
              Ad Group
            </label>
            <select
              value={selectedAdGroup}
              onChange={(e) => setSelectedAdGroup(e.target.value)}
              disabled={!selectedCampaign || adGroups.length === 0 || loadingAdGroups}
              style={{
                width: '100%',
                padding: '12px',
                background: c.bg.tertiary,
                border: `1px solid ${c.border.primary}`,
                borderRadius: '8px',
                color: c.text.primary,
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">
                {loadingAdGroups ? 'Loading...' : 'Select an ad group...'}
              </option>
              {adGroups.map(adGroup => (
                <option key={adGroup.id} value={adGroup.id}>
                  {adGroup.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {loadingDetails && (
          <div style={{
            marginTop: '20px',
            padding: '16px',
            background: c.bg.tertiary,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: c.text.secondary
          }}>
            <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
            <span>Loading ad group data and analyzing...</span>
          </div>
        )}
      </div>
      
      {/* Analysis Results */}
      {analysis && !analyzing && (
        <>
          {/* Priority Banner */}
          <div style={{
            background: c.bg.secondary,
            border: `1px solid ${c.border.primary}`,
            borderLeft: `3px solid ${analysis.nextAction.priority === 'critical' ? '#dc2626' : '#f59e0b'}`,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{
                background: analysis.nextAction.priority === 'critical' ? '#dc2626' : '#f59e0b',
                borderRadius: '8px',
                padding: '12px',
                flexShrink: 0
              }}>
                <Zap size={24} color="white" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '8px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: c.text.primary,
                    margin: 0
                  }}>
                    {analysis.nextAction.phase}
                  </h3>
                  <span style={{
                    background: getPriorityColor(analysis.priorityLevel),
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {analysis.priorityLevel} Priority
                  </span>
                </div>
                <p style={{ color: c.text.secondary, marginBottom: '16px' }}>
                  {analysis.nextAction.message}
                </p>
                
                {/* Issues/Gaps */}
                {analysis.nextAction.issues && analysis.nextAction.issues.length > 0 && (
                  <div>
                    {analysis.nextAction.issues.map((issue, idx) => (
                      <div key={idx} style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px'
                        }}>
                          <AlertCircle size={16} color="#ef4444" />
                          <span style={{ fontWeight: '600', color: c.text.primary }}>
                            {issue.message}
                          </span>
                        </div>
                        <p style={{ color: c.text.secondary, fontSize: '14px', margin: '4px 0 0 24px' }}>
                          → {issue.recommendation}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                
                {analysis.nextAction.gaps && analysis.nextAction.gaps.length > 0 && (
                  <div>
                    {analysis.nextAction.gaps.map((gap, idx) => (
                      <div key={idx} style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px'
                        }}>
                          <AlertCircle size={16} color="#ef4444" />
                          <span style={{ fontWeight: '600', color: c.text.primary }}>
                            {gap.message}
                          </span>
                        </div>
                        <p style={{ color: c.text.secondary, fontSize: '14px', margin: '4px 0 0 24px' }}>
                          → {gap.recommendation}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Component Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '24px',
            marginBottom: '32px'
          }}>
            {/* Ad Relevance */}
            <div style={{
              background: c.bg.secondary,
              borderRadius: '16px',
              padding: '24px',
              border: `1px solid ${c.border.primary}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: c.text.primary }}>
                  Ad Relevance
                </h3>
                {getStatusIcon(analysis.components.adRelevance.status)}
              </div>
              
              <div style={{
                fontSize: '36px',
                fontWeight: '700',
                color: getStatusColor(analysis.components.adRelevance.status),
                marginBottom: '8px'
              }}>
                {analysis.components.adRelevance.score}/100
              </div>
              
              <p style={{ color: c.text.secondary, marginBottom: '16px' }}>
                {analysis.components.adRelevance.summary}
              </p>
              
              {analysis.components.adRelevance.semanticDistribution && (
                <div style={{
                  background: c.bg.tertiary,
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{ color: c.text.secondary, fontSize: '14px' }}>
                      Keyword Coverage
                    </span>
                    <span style={{
                      color: c.text.primary,
                      fontWeight: '600'
                    }}>
                      {analysis.components.adRelevance.semanticDistribution.coverage}%
                    </span>
                  </div>
                  <div style={{
                    background: c.border.primary,
                    height: '8px',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${analysis.components.adRelevance.semanticDistribution.coverage}%`,
                      height: '100%',
                      background: getStatusColor(analysis.components.adRelevance.status),
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
            </div>
            
            {/* Expected CTR */}
            <div style={{
              background: c.bg.secondary,
              borderRadius: '16px',
              padding: '24px',
              border: `1px solid ${c.border.primary}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: c.text.primary }}>
                  Expected CTR
                </h3>
                {getStatusIcon(analysis.components.expectedCTR.status)}
              </div>
              
              <div style={{
                fontSize: '36px',
                fontWeight: '700',
                color: getStatusColor(analysis.components.expectedCTR.status),
                marginBottom: '8px'
              }}>
                {analysis.components.expectedCTR.score}/100
              </div>
              
              <p style={{ color: c.text.secondary, marginBottom: '16px' }}>
                {analysis.components.expectedCTR.summary}
              </p>
              
              {analysis.components.expectedCTR.fiveSecondTest && (
                <div style={{
                  background: c.bg.tertiary,
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '12px'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: c.text.primary, marginBottom: '8px' }}>
                    5-Second Test
                  </div>
                  {Object.entries(analysis.components.expectedCTR.fiveSecondTest).map(([key, value]) => (
                    <div key={key} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      {value ? (
                        <CheckCircle size={14} color="#10b981" />
                      ) : (
                        <XCircle size={14} color="#ef4444" />
                      )}
                      <span style={{
                        color: c.text.secondary,
                        fontSize: '13px',
                        textTransform: 'capitalize'
                      }}>
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Landing Page Experience */}
            <div style={{
              background: c.bg.secondary,
              borderRadius: '16px',
              padding: '24px',
              border: `1px solid ${c.border.primary}`
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: c.text.primary }}>
                  Landing Page
                </h3>
                {getStatusIcon(analysis.components.landingPageExperience.status)}
              </div>
              
              <div style={{
                fontSize: '36px',
                fontWeight: '700',
                color: getStatusColor(analysis.components.landingPageExperience.status),
                marginBottom: '8px'
              }}>
                {analysis.components.landingPageExperience.score}/100
              </div>
              
              <p style={{ color: c.text.secondary }}>
                {analysis.components.landingPageExperience.summary}
              </p>
            </div>
          </div>
          
          {/* Generate Improvements Button */}
          {!improvements && (
            <div style={{ marginTop: '32px', textAlign: 'center' }}>
              <button
                onClick={generateImprovements}
                disabled={generatingImprovements}
                style={{
                  padding: '16px 32px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: generatingImprovements ? 'wait' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.3s ease',
                  opacity: generatingImprovements ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!generatingImprovements) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.3)';
                }}
              >
                {generatingImprovements ? (
                  <>
                    <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    Generating AI Improvements...
                  </>
                ) : (
                  <>
                    <Zap size={20} />
                    Generate AI Improvements
                  </>
                )}
              </button>
            </div>
          )}
          
          {/* Improvements Section */}
          {improvements && (
            <div style={{
              marginTop: '32px',
              background: c.bg.secondary,
              border: `1px solid ${c.border.primary}`,
              borderRadius: '16px',
              padding: '32px',
              borderTop: '3px solid #10b981'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Zap size={24} color="#fff" />
                </div>
                <div>
                  <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px', color: c.text.primary }}>
                    AI-Generated Improvements
                  </h2>
                  <p style={{ color: c.text.secondary, margin: 0 }}>
                    {improvements.reasoning}
                  </p>
                </div>
              </div>
              
              {/* Improvements List */}
              <div style={{
                background: c.bg.tertiary,
                border: `1px solid ${c.border.primary}`,
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: c.text.primary }}>
                  What's Improved:
                </h3>
                <ul style={{ paddingLeft: '20px', margin: 0, color: c.text.secondary }}>
                  {improvements.improvements.map((imp, idx) => (
                    <li key={idx} style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                      {imp}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Editable Headlines */}
              <div style={{
                background: c.bg.tertiary,
                border: `1px solid ${c.border.primary}`,
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: c.text.primary }}>
                    New Headlines ({improvements.headlines.length}):
                  </h3>
                  <span style={{ color: c.text.tertiary, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Edit3 size={14} />
                    Click to edit
                  </span>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {improvements.headlines.map((headline, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: c.bg.secondary,
                        border: `1px solid ${c.border.primary}`,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <input
                        type="text"
                        value={headline}
                        onChange={(e) => {
                          const newHeadlines = [...improvements.headlines];
                          newHeadlines[idx] = e.target.value;
                          setImprovements({ ...improvements, headlines: newHeadlines });
                        }}
                        maxLength={30}
                        style={{
                          flex: 1,
                          background: 'transparent',
                          border: 'none',
                          color: c.text.primary,
                          padding: '12px 16px',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                      />
                      <span style={{ 
                        fontSize: '12px',
                        color: headline.length > 30 ? '#ef4444' : c.text.tertiary,
                        paddingRight: '8px'
                      }}>
                        {headline.length}/30
                      </span>
                      {improvements.headlines.length > 3 && (
                        <button
                          onClick={() => {
                            const newHeadlines = improvements.headlines.filter((_, i) => i !== idx);
                            setImprovements({ ...improvements, headlines: newHeadlines });
                          }}
                          style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px',
                            cursor: 'pointer',
                            marginRight: '8px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Remove headline"
                        >
                          <Trash2 size={14} color="#ef4444" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {improvements.headlines.length < 15 && (
                  <button
                    onClick={() => {
                      setImprovements({ 
                        ...improvements, 
                        headlines: [...improvements.headlines, ''] 
                      });
                    }}
                    style={{
                      marginTop: '12px',
                      background: c.bg.secondary,
                      border: `2px dashed ${c.border.primary}`,
                      borderRadius: '8px',
                      padding: '10px 16px',
                      color: c.text.secondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      justifyContent: 'center',
                      fontSize: '14px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = c.bg.hover}
                    onMouseLeave={(e) => e.currentTarget.style.background = c.bg.secondary}
                  >
                    <Plus size={16} />
                    Add Headline
                  </button>
                )}
                <p style={{ fontSize: '12px', color: c.text.muted, marginTop: '8px', marginBottom: 0 }}>
                  Min 3, max 15 headlines. Google recommends 8-15 for best performance.
                </p>
              </div>
              
              {/* Editable Descriptions */}
              <div style={{
                background: c.bg.tertiary,
                border: `1px solid ${c.border.primary}`,
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: c.text.primary }}>
                    New Descriptions ({improvements.descriptions.length}):
                  </h3>
                  <span style={{ color: c.text.tertiary, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Edit3 size={14} />
                    Click to edit
                  </span>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {improvements.descriptions.map((desc, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: c.bg.secondary,
                        border: `1px solid ${c.border.primary}`,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px'
                      }}
                    >
                      <textarea
                        value={desc}
                        onChange={(e) => {
                          const newDescriptions = [...improvements.descriptions];
                          newDescriptions[idx] = e.target.value;
                          setImprovements({ ...improvements, descriptions: newDescriptions });
                        }}
                        maxLength={90}
                        rows={2}
                        style={{
                          flex: 1,
                          background: 'transparent',
                          border: 'none',
                          color: c.text.primary,
                          padding: '12px 16px',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          outline: 'none',
                          resize: 'none',
                          lineHeight: '1.5'
                        }}
                      />
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        gap: '8px',
                        paddingTop: '12px',
                        paddingRight: '8px'
                      }}>
                        <span style={{ 
                          fontSize: '12px',
                          color: desc.length > 90 ? '#ef4444' : c.text.tertiary
                        }}>
                          {desc.length}/90
                        </span>
                        {improvements.descriptions.length > 2 && (
                          <button
                            onClick={() => {
                              const newDescriptions = improvements.descriptions.filter((_, i) => i !== idx);
                              setImprovements({ ...improvements, descriptions: newDescriptions });
                            }}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Remove description"
                          >
                            <Trash2 size={14} color="#ef4444" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {improvements.descriptions.length < 4 && (
                  <button
                    onClick={() => {
                      setImprovements({ 
                        ...improvements, 
                        descriptions: [...improvements.descriptions, ''] 
                      });
                    }}
                    style={{
                      marginTop: '12px',
                      background: c.bg.secondary,
                      border: `2px dashed ${c.border.primary}`,
                      borderRadius: '8px',
                      padding: '10px 16px',
                      color: c.text.secondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: '100%',
                      justifyContent: 'center',
                      fontSize: '14px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = c.bg.hover}
                    onMouseLeave={(e) => e.currentTarget.style.background = c.bg.secondary}
                  >
                    <Plus size={16} />
                    Add Description
                  </button>
                )}
                <p style={{ fontSize: '12px', color: c.text.muted, marginTop: '8px', marginBottom: 0 }}>
                  Min 2, max 4 descriptions. Google recommends 3-4 for best performance.
                </p>
              </div>
              
              {/* Push to Ads Button */}
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <button
                  onClick={() => setShowPushConfirm(true)}
                  disabled={pushing}
                  style={{
                    padding: '16px 32px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: pushing ? 'wait' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '12px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.3s ease',
                    opacity: pushing ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!pushing) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  <ChevronRight size={20} />
                  Push to Google Ads as A/B Test
                </button>
                
                <button
                  onClick={() => {
                    setImprovements(null);
                    setPushSuccess(false);
                  }}
                  style={{
                    padding: '16px 32px',
                    background: c.bg.tertiary,
                    color: c.text.primary,
                    border: `1px solid ${c.border.primary}`,
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = c.bg.hover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = c.bg.tertiary;
                  }}
                >
                  Regenerate
                </button>
              </div>
            </div>
          )}
          
          {/* Push Confirmation Modal */}
          {showPushConfirm && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: c.bg.secondary,
                borderRadius: '16px',
                padding: '32px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '20px',
                  color: c.text.primary
                }}>
                  <AlertTriangle size={32} color="#f59e0b" />
                  <h2 style={{ fontSize: '24px', fontWeight: '700' }}>
                    Push to Google Ads?
                  </h2>
                </div>
                
                <p style={{ color: c.text.secondary, lineHeight: '1.6', marginBottom: '24px' }}>
                  This will create a <strong>new Responsive Search Ad</strong> in ad group <strong>"{adGroupData.name}"</strong> with the improved copy.
                  <br /><br />
                  Google Ads will automatically rotate it with your existing ads as an <strong>A/B test</strong>.
                  <br /><br />
                  The ad will be labeled <strong>"Bidoryx Optimized"</strong> for easy tracking.
                </p>
                
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={pushToGoogleAds}
                    disabled={pushing}
                    style={{
                      flex: 1,
                      padding: '14px 24px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: pushing ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: pushing ? 0.7 : 1
                    }}
                  >
                    {pushing ? (
                      <>
                        <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                        Pushing...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Yes, Push to Ads
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => setShowPushConfirm(false)}
                    disabled={pushing}
                    style={{
                      flex: 1,
                      padding: '14px 24px',
                      background: c.bg.tertiary,
                      color: c.text.primary,
                      border: `1px solid ${c.border.primary}`,
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: pushing ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Success Modal */}
          {pushSuccess && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001
            }}>
              <div style={{
                background: c.bg.secondary,
                borderRadius: '16px',
                padding: '40px',
                maxWidth: '480px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}>
                  <CheckCircle size={36} color="white" />
                </div>
                
                <h2 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: c.text.primary,
                  marginBottom: '12px'
                }}>
                  Successfully Pushed!
                </h2>
                
                <p style={{
                  color: c.text.secondary,
                  lineHeight: '1.6',
                  marginBottom: '8px',
                  fontSize: '15px'
                }}>
                  A new Responsive Search Ad has been created in ad group <strong style={{ color: c.text.secondary }}>"{adGroupData?.name}"</strong>.
                </p>
                <p style={{
                  color: c.text.secondary,
                  lineHeight: '1.6',
                  marginBottom: '8px',
                  fontSize: '15px'
                }}>
                  Google Ads will automatically A/B test it against your existing ads.
                </p>
                <p style={{
                  color: '#059669',
                  lineHeight: '1.6',
                  marginBottom: '12px',
                  fontSize: '14px'
                }}>
                  Look for the <strong>"Bidoryx Optimized"</strong> label in your Google Ads interface.
                </p>
                <p style={{
                  color: c.text.secondary,
                  lineHeight: '1.6',
                  marginBottom: '28px',
                  fontSize: '13px'
                }}>
                  An A/B test has been automatically created in the <strong style={{ color: c.text.secondary }}>A/B Testing Lab</strong> (Advanced menu) to track performance.
                </p>
                
                <button
                  onClick={() => {
                    setPushSuccess(false);
                    setImprovements(null);
                    setAnalysis(null);
                    setAdGroupData(null);
                    setSelectedAdGroup('');
                    setSelectedCampaign('');
                    setAdGroups([]);
                  }}
                  style={{
                    padding: '14px 40px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  <RefreshCw size={18} />
                  Optimize Another Ad Group
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
